import { motion } from 'framer-motion';
import { useState } from 'react';
import { staggerContainer, staggerItem } from '../../utils/animations';

const ExcerptBlock = () => {
  const [activeTab, setActiveTab] = useState('micro');

  return (
    <div>
      {/* Edition Tabs */}
      <div className="flex justify-center gap-4 mb-8">
        <motion.button
          onClick={() => setActiveTab('micro')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`px-8 py-3 rounded-button font-display font-bold text-lg transition-all ${
            activeTab === 'micro'
              ? 'bg-primary-green text-white shadow-card'
              : 'bg-white text-primary-green border-2 border-primary-green'
          }`}
        >
          🌈 Micro Edition (4-9 years)
        </motion.button>
        <motion.button
          onClick={() => setActiveTab('macro')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`px-8 py-3 rounded-button font-display font-bold text-lg transition-all ${
            activeTab === 'macro'
              ? 'bg-primary-green text-white shadow-card'
              : 'bg-white text-primary-green border-2 border-primary-green'
          }`}
        >
          🚀 Macro Edition (10-15 years)
        </motion.button>
      </div>

      {/* Content Based on Active Tab */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {activeTab === 'micro' ? <MicroExcerpts /> : <MacroExcerpts />}
      </motion.div>
    </div>
  );
};

// Micro Edition Excerpts
const MicroExcerpts = () => {
  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      {microContent.map((content, index) => (
        <motion.div
          key={index}
          variants={staggerItem}
          className="card bg-white hover:shadow-card-hover"
        >
          {/* Content Type Badge */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-3xl">{content.icon}</span>
            <span className="bg-accent-orange/20 text-accent-orange px-3 py-1 rounded-full text-sm font-display font-bold">
              {content.type}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-display font-bold text-primary-green-dark mb-3">
            {content.title}
          </h3>

          {/* Excerpt */}
          <div className="bg-accent-blue/10 rounded-lg p-4 mb-4">
            <p className="text-neutral-gray-dark leading-relaxed font-medium italic">
              "{content.excerpt}"
            </p>
          </div>

          {/* Language Mix Example */}
          {content.languageMix && (
            <div className="border-l-4 border-primary-green pl-4">
              <p className="text-sm text-neutral-gray-dark mb-2">
                <strong className="text-primary-green">Multilingual Learning:</strong>
              </p>
              <p className="text-neutral-gray-dark">
                {content.languageMix}
              </p>
            </div>
          )}
        </motion.div>
      ))}
    </motion.div>
  );
};

// Macro Edition Excerpts
const MacroExcerpts = () => {
  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      {macroContent.map((content, index) => (
        <motion.div
          key={index}
          variants={staggerItem}
          className="card bg-white hover:shadow-card-hover"
        >
          {/* Content Type Badge */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-3xl">{content.icon}</span>
            <span className="bg-primary-green/20 text-primary-green px-3 py-1 rounded-full text-sm font-display font-bold">
              {content.type}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-display font-bold text-primary-green-dark mb-3">
            {content.title}
          </h3>

          {/* Excerpt */}
          <div className="bg-accent-blue/10 rounded-lg p-4 mb-4">
            <p className="text-neutral-gray-dark leading-relaxed font-medium italic">
              "{content.excerpt}"
            </p>
          </div>

          {/* Additional Info */}
          {content.info && (
            <div className="border-l-4 border-accent-orange pl-4">
              <p className="text-sm text-neutral-gray-dark">
                <strong className="text-accent-orange">Did you know?</strong> {content.info}
              </p>
            </div>
          )}
        </motion.div>
      ))}
    </motion.div>
  );
};

// Micro Edition Sample Content
const microContent = [
  {
    icon: '📚',
    type: 'Story Time',
    title: 'The Brave Little Turtle',
    excerpt: 'Timmy the turtle wanted to cross the big pond. His friends said, "You can\'t do it!" But Timmy knew he could try...',
    languageMix: 'English story with Hindi words: "Dost" (Friend), "Himmat" (Courage), "Khushi" (Happiness)',
  },
  {
    icon: '🧩',
    type: 'Puzzle Corner',
    title: 'Rainbow Maze',
    excerpt: 'Help Bunny find all the colorful carrots! Connect the dots and solve the rainbow path.',
    languageMix: 'Numbers in English, Hindi & Telugu: One-Ek-Okati, Two-Do-Rendu',
  },
  {
    icon: '🎨',
    type: 'Creative Fun',
    title: 'Draw Your Dream House',
    excerpt: 'What does your dream house look like? Draw it with bright colors and show us your imagination!',
  },
  {
    icon: '🔢',
    type: 'Math Magic',
    title: 'Counting Stars',
    excerpt: 'How many stars can you count? Let\'s practice numbers 1 to 20 with our friendly star guide!',
    languageMix: 'Counting in multiple languages makes learning fun!',
  },
];

// Macro Edition Sample Content
const macroContent = [
  {
    icon: '📰',
    type: 'News Bytes',
    title: 'Young Scientist Wins Award',
    excerpt: 'Meet Priya, a 13-year-old who invented a water purification system using solar energy. Her innovation is helping villages across India...',
    info: 'We feature real stories of young achievers to inspire our readers!',
  },
  {
    icon: '🧠',
    type: 'Brain Teaser',
    title: 'The Logic Puzzle',
    excerpt: 'Three friends ordered pizza. If Alex ate 1/3, Sam ate 1/4, and Jamie ate 5 slices, how many slices were in the pizza?',
    info: 'Our puzzles are designed by education experts to build critical thinking.',
  },
  {
    icon: '✍️',
    type: 'Creative Writing',
    title: 'Story Starters',
    excerpt: 'Complete this story: "The clock struck midnight, and suddenly my pet cat started talking! It said..."',
    info: 'Winners get published in our next edition and receive certificates!',
  },
  {
    icon: '🌍',
    type: 'World Facts',
    title: 'Amazing Geography',
    excerpt: 'Did you know the Amazon rainforest produces 20% of the world\'s oxygen? Let\'s explore why rainforests are called the "lungs of Earth"...',
    info: 'Each edition explores different countries and cultures.',
  },
];

export default ExcerptBlock;